/**
 * 動画評価（参考になった／参考にならなかった）トラッキング用のユーティリティ関数
 */

import { publicApiEndpoint } from './env'
import { getOrCreateSessionId } from './analytics'

export type VideoFeedbackSource = 'EMBED' | 'VIDEO_DETAIL'

interface VideoFeedbackEvent {
  videoId: string
  sessionId: string
  isHelpful: boolean
  source: VideoFeedbackSource
  scenarioId?: string
  userAgent?: string
}

const VOTED_VIDEOS_KEY = 'moovy_video_feedback_voted'
const SKIPPED_VIDEOS_KEY = 'moovy_video_feedback_skipped'

/**
 * 指定した動画に対して既に評価済みかどうかを判定
 */
export function hasVotedVideo(videoId: string): boolean {
  if (typeof window === 'undefined') return false
  try {
    const stored = localStorage.getItem(VOTED_VIDEOS_KEY)
    if (!stored) return false
    const votedIds = JSON.parse(stored) as unknown
    return Array.isArray(votedIds) && votedIds.includes(videoId)
  } catch (error) {
    console.warn('Failed to read voted video feedback list:', error)
    return false
  }
}

/**
 * 指定した動画を評価済みとして記録
 */
export function markVotedVideo(videoId: string): void {
  if (typeof window === 'undefined') return
  try {
    const stored = localStorage.getItem(VOTED_VIDEOS_KEY)
    const votedIds = stored ? (JSON.parse(stored) as unknown) : []
    const list = Array.isArray(votedIds) ? (votedIds as string[]) : []
    if (!list.includes(videoId)) {
      localStorage.setItem(VOTED_VIDEOS_KEY, JSON.stringify([...list, videoId]))
    }
  } catch (error) {
    console.warn('Failed to store voted video feedback list:', error)
  }
}

/**
 * 評価をスキップした（回答せず閉じた）動画かどうかを判定。
 *
 * スキップ後に視聴者が手動でもう一度再生すると、終了時にまた評価画面が出てしまう。
 * 「答えたくない」と示した相手に同じ設問を出し直さないための記録。
 * 回答（localStorage・永続）と違い、`sessionStorage` にだけ残して次の訪問では再び聞く。
 */
export function hasSkippedVideoFeedback(videoId: string): boolean {
  if (typeof window === 'undefined') return false
  try {
    const stored = sessionStorage.getItem(SKIPPED_VIDEOS_KEY)
    if (!stored) return false
    const skippedIds = JSON.parse(stored) as unknown
    return Array.isArray(skippedIds) && skippedIds.includes(videoId)
  } catch (error) {
    console.warn('Failed to read skipped video feedback list:', error)
    return false
  }
}

/**
 * 評価をスキップした動画として記録（このセッション中のみ）
 */
export function markSkippedVideoFeedback(videoId: string): void {
  if (typeof window === 'undefined') return
  try {
    const stored = sessionStorage.getItem(SKIPPED_VIDEOS_KEY)
    const skippedIds = stored ? (JSON.parse(stored) as unknown) : []
    const list = Array.isArray(skippedIds) ? (skippedIds as string[]) : []
    if (!list.includes(videoId)) {
      sessionStorage.setItem(SKIPPED_VIDEOS_KEY, JSON.stringify([...list, videoId]))
    }
  } catch (error) {
    console.warn('Failed to store skipped video feedback list:', error)
  }
}

/**
 * 動画評価を送信
 * 同一動画・同一セッションからの評価は1件に集約される（後から変更可能）。
 */
export async function trackVideoFeedback(
  videoId: string,
  isHelpful: boolean,
  source: VideoFeedbackSource,
  scenarioId?: string,
): Promise<void> {
  const sessionId = getOrCreateSessionId()

  try {
    const fullEvent: VideoFeedbackEvent = {
      videoId,
      sessionId,
      isHelpful,
      source,
      scenarioId,
      userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : undefined,
    }

    if (process.env.NODE_ENV === 'development') {
      console.log('[Video Feedback Event]', fullEvent)
    }

    const response = await fetch(`${publicApiEndpoint}/video-feedback`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(fullEvent),
    })

    if (!response.ok) {
      console.warn('Video feedback API returned non-OK status:', response.status)
    }
  } catch (error) {
    console.error('Failed to track video feedback:', error)
    // イベント送信の失敗はユーザー体験に影響しないため、エラーを握りつぶす
  }
}
